import { comparer, makeAutoObservable, runInAction } from "mobx";
import { type AnalyticsSource, track } from "@/lib/analytics";
import { DASHBOARD_PREVIEW_BATCH } from "@/services/dashboard.service";
import type { RootStore } from "./root.store";
import type {
  ChartResponse,
  Dashboard,
  DashboardCreateBody,
  DashboardPreview,
  DashboardShare,
  DashboardShareCreateBody,
  DashboardShareRole,
  DashboardWidget,
  SupportedFilterKey,
  UserCapacityBreakdown,
  WidgetCreateBody,
  WidgetSourceMetadata,
  WidgetUpdateBody,
} from "@/types/dashboard";

// Canonical left-to-right order of filter chips. Mirrors the backend's
// ``CANONICAL_KEYS`` (``cognota/dashboards/filters.py``) so the toolbar renders
// the per-dashboard filter union in the same predictable order the BE used to
// return from the retired ``supported-filters/`` endpoint (#2018).
const CANONICAL_FILTER_ORDER: readonly SupportedFilterKey[] = [
  "created_at_range",
  "assignees",
  "projects",
  "programs",
  "goals",
  "category",
  "request_category",
  "request_type",
  "state_group",
  "lob",
  "teams",
  "home_teams",
  "custom_field",
];

type DashboardUpdateBody = {
  name?: string;
  description?: string;
  project_ids?: string[];
  access?: 0 | 1;
};

/**
 * The keys of an update whose value differs from the stored dashboard:
 * `dashboard_updated`'s `fields`. The edit modal sends every field on every save,
 * so the body's keys alone would name all of them each time. `project_ids` is
 * compared, order-free, with the stored `projects`. A dashboard that is not in
 * the map yet counts every key it sends.
 */
function changedDashboardFields(
  body: DashboardUpdateBody,
  before: Dashboard | undefined,
): string[] {
  return (Object.keys(body) as (keyof DashboardUpdateBody)[]).filter((key) => {
    if (!before) return true;
    if (key === "project_ids") {
      const was = (before.projects ?? []).map((p) => p.id).sort();
      return !comparer.structural([...(body.project_ids ?? [])].sort(), was);
    }
    return !comparer.structural(body[key], before[key]);
  });
}

// How many times fetchWidgets reads a dashboard's widget list before giving up
// on one that no widget mutation overtook (NG-2074). A read is repeated only
// when a widget save finished while the previous read was in flight, so this
// bounds someone saving continuously through a slow load, not normal use.
const MAX_WIDGET_LIST_READS = 5;

export class DashboardStore {
  rootStore: RootStore;

  dashboards = new Map<string, Dashboard>();
  /** Chart previews for the LIST cards, keyed by dashboard id. Filled by a
   *  separate call from the one that loads the list, and memoised for the
   *  session so returning to the list costs nothing. */
  previewsByDashboard = new Map<string, DashboardPreview>();
  loadingPreviews = false;

  /** Dashboards whose previews are in flight. Per-card rather than one flag for
   *  the screen: cards are asked about as they reach the viewport, so at any
   *  moment some are loaded, some are pending, and some have not been asked
   *  about at all — and only the pending ones should show a skeleton. */
  pendingPreviewIds = new Set<string>();
  widgetsByDashboard = new Map<string, DashboardWidget[]>();
  chartByWidget = new Map<string, ChartResponse>();

  /** Per-user shares of a dashboard, keyed by dashboard id — the rows the Share
   *  modal renders (NG-1115). Loaded on demand when the modal opens. */
  sharesByDashboard = new Map<string, DashboardShare[]>();
  loadingShares = new Map<string, boolean>();

  loadingDashboards = false;
  loadingDashboard = new Map<string, boolean>();
  loadingWidgets = new Map<string, boolean>();
  loadingChart = new Map<string, boolean>();
  savingDashboard = new Map<string, boolean>();
  savingDashboardFilters = new Map<string, boolean>();

  errorDashboards: Error | null = null;
  errorDashboard = new Map<string, Error | null>();
  errorWidgets = new Map<string, Error | null>();
  errorChart = new Map<string, Error | null>();
  errorSavingDashboard = new Map<string, Error | null>();
  errorSavingDashboardFilters = new Map<string, Error | null>();

  // Per-widget request token. Bumped on every fetchChart so a late response
  // from a stale request (filter change, fast nav) is dropped instead of
  // overwriting fresh data.
  private chartFetchVersion = new Map<string, number>();

  // Per-dashboard widget-list generation (NG-2074). Every widget mutation that
  // writes widgetsByDashboard bumps it. fetchWidgets reads it before its GET,
  // and if it has moved by the time the list arrives, that list was read before
  // the mutation landed: writing it would erase a just-created widget, undo an
  // edit, or bring back a deleted one. So the list is refetched instead.
  private widgetListGeneration = new Map<string, number>();

  // Dashboard-filter saves are serialized per dashboard. Two quick chip
  // changes must reach the backend in the same order the user made them; two
  // overlapping PATCHes can otherwise finish backwards and leave the shared
  // default at the older selection. The version makes only the newest queued
  // result/error observable to the page.
  private dashboardFilterSaveQueue = new Map<string, Promise<Dashboard>>();
  private dashboardFilterSaveVersion = new Map<string, number>();

  // Capacity PR3 — W4 drill-down state. ``expandedUserId`` is keyed by widget
  // because each W3 instance has its own expansion. ``breakdownByUser`` is
  // keyed by ``user_id`` (the bar identity).
  expandedUserByWidget = new Map<string, string | null>();
  breakdownByUser = new Map<string, UserCapacityBreakdown>();
  loadingBreakdown = new Map<string, boolean>();

  // #1972 — per-source builder capabilities served by widget-metadata/.
  // Workspace-static today, so a single fetch per workspace session is cached.
  metadataBySource = new Map<string, WidgetSourceMetadata>();
  loadingWidgetMetadata = false;
  widgetMetadataSlug: string | null = null;

  // Per-widget "All / over limit" capacity scope OVERRIDE (June 12 daily).
  // Only holds a value once the user toggles a widget; absent means "use the
  // widget's config default" (the BE resolves that), so fetchChart sends the
  // ``capacity_scope`` param only for widgets the user explicitly toggled.
  capacityScopeByWidget = new Map<string, "all" | "over_limit">();

  // #1082: the user whose detail side panel is open, set when an owner/lead
  // name is clicked in a table widget. Null = panel closed. One panel serves
  // the whole dashboard, so this is page-scoped, not per-widget.
  panelMemberId: string | null = null;

  /** The widget whose config editor panel is open (detail page). Null = closed. */
  selectedWidgetId: string | null = null;

  /** Whether the editor should put the caret in the Name field when it opens.
   *  Set with the selection rather than beside it: the panel reads it on the
   *  render where `open` flips, and its focus manager acts once, so a signal
   *  arriving a render later is a signal that arrives too late. */
  focusNameOnOpen = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  async fetchDashboards(slug: string): Promise<void> {
    this.loadingDashboards = true;
    this.errorDashboards = null;
    try {
      const list = await this.rootStore.dashboardService.list(slug);
      runInAction(() => {
        this.dashboards.clear();
        for (const d of list) this.dashboards.set(d.id, d);
      });
    } catch (err) {
      console.error("dashboard.store: fetchDashboards failed", { slug, err });
      runInAction(() => {
        this.errorDashboards = err as Error;
      });
    } finally {
      runInAction(() => {
        this.loadingDashboards = false;
      });
    }
  }

  /** Load previews for the dashboards currently on screen.
   *
   * Skips ids already held, so scrolling a long list asks only about cards it
   * has not seen. Best-effort by design: on failure the cards simply render
   * without previews, and the list — which came from a different request —
   * is untouched.
   *
   * Asked in batches, because the endpoint caps how many dashboards one call
   * may cover and rejects a longer list outright. Sending every id in one call
   * meant a workspace past the cap got a 400 and therefore NO previews at all —
   * not the first N — so every card looked empty however many widgets it held.
   * Batches settle independently: one failing leaves the rest on screen.
   */
  async fetchPreviews(slug: string, dashboardIds: string[]): Promise<void> {
    const missing = dashboardIds.filter((id) => !this.previewsByDashboard.has(id));
    if (missing.length === 0) return;

    const batches: string[][] = [];
    for (let i = 0; i < missing.length; i += DASHBOARD_PREVIEW_BATCH) {
      batches.push(missing.slice(i, i + DASHBOARD_PREVIEW_BATCH));
    }

    this.loadingPreviews = true;
    runInAction(() => {
      for (const id of missing) this.pendingPreviewIds.add(id);
    });
    try {
      const results = await Promise.allSettled(
        batches.map((batch) => this.rootStore.dashboardService.listPreviews(slug, batch)),
      );
      runInAction(() => {
        for (const [i, result] of results.entries()) {
          if (result.status === "rejected") {
            console.warn("dashboard previews failed to load", {
              slug,
              dashboardIds: batches[i],
              err: result.reason,
            });
            continue;
          }
          for (const [id, preview] of Object.entries(result.value.previews)) {
            this.previewsByDashboard.set(id, preview);
          }
        }
      });
    } finally {
      runInAction(() => {
        for (const id of missing) this.pendingPreviewIds.delete(id);
        this.loadingPreviews = this.pendingPreviewIds.size > 0;
      });
    }
  }

  /** Whether this dashboard's preview is being fetched right now. False both
   *  before it is asked about and after a failed attempt — a card with nothing
   *  in flight shows a blank strip rather than a skeleton that never resolves. */
  isPreviewPending(dashboardId: string): boolean {
    return this.pendingPreviewIds.has(dashboardId);
  }

  async fetchDashboard(slug: string, id: string): Promise<void> {
    this.loadingDashboard.set(id, true);
    this.errorDashboard.set(id, null);
    try {
      const d = await this.rootStore.dashboardService.retrieve(slug, id);
      runInAction(() => {
        this.dashboards.set(d.id, d);
      });
    } catch (err) {
      console.error("dashboard.store: fetchDashboard failed", { slug, id, err });
      runInAction(() => {
        this.errorDashboard.set(id, err as Error);
      });
    } finally {
      runInAction(() => {
        this.loadingDashboard.set(id, false);
      });
    }
  }

  /** Load a dashboard's widget list.
   *
   * The detail route starts this without awaiting it, so the board is live,
   * Create widget included, while the list is still in flight. A list that
   * arrives after a widget mutation was read before that mutation, so it is
   * dropped and read again rather than written over it (NG-2074). The promise
   * settles only once a list is kept, so a caller chaining off it (the route's
   * refetchAllCharts) works on the list that is actually on the board. */
  async fetchWidgets(slug: string, dashboardId: string): Promise<void> {
    this.loadingWidgets.set(dashboardId, true);
    this.errorWidgets.set(dashboardId, null);
    try {
      for (let read = 1; read <= MAX_WIDGET_LIST_READS; read++) {
        const generation = runInAction(() => this.widgetListGeneration.get(dashboardId) ?? 0);
        const list = await this.rootStore.dashboardService.listWidgets(slug, dashboardId);
        const kept = runInAction(() => {
          if ((this.widgetListGeneration.get(dashboardId) ?? 0) !== generation) return false;
          this.widgetsByDashboard.set(dashboardId, list);
          return true;
        });
        if (kept) return;
        console.warn("dashboard.store: fetchWidgets dropped a list a widget mutation overtook", {
          slug,
          dashboardId,
          read,
          maxReads: MAX_WIDGET_LIST_READS,
        });
      }
      // Leave the list the mutations built rather than write a stale one over
      // it, and fail through the catch below so the give-up is logged and kept.
      throw new Error(
        `every widget list read was overtaken by a widget mutation (${MAX_WIDGET_LIST_READS} reads)`,
      );
    } catch (err) {
      console.error("dashboard.store: fetchWidgets failed", { slug, dashboardId, err });
      runInAction(() => {
        this.errorWidgets.set(dashboardId, err as Error);
      });
    } finally {
      runInAction(() => {
        this.loadingWidgets.set(dashboardId, false);
      });
    }
  }

  /** Record that a mutation just wrote this dashboard's widget list, so a list
   *  read already in flight is dropped instead of written over it (NG-2074).
   *  Call it from the action that applies the mutation's result. */
  private bumpWidgetListGeneration(dashboardId: string): void {
    this.widgetListGeneration.set(
      dashboardId,
      (this.widgetListGeneration.get(dashboardId) ?? 0) + 1,
    );
  }

  /**
   * Every write below takes the `source` of the surface that made it, for its
   * analytics event. It defaults to `page`, the resource's own page; any other
   * surface passes its own. Each event fires after the write succeeds, never on
   * failure.
   */
  async updateDashboard(
    slug: string,
    id: string,
    body: DashboardUpdateBody,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.savingDashboard.set(id, true);
    this.errorSavingDashboard.set(id, null);
    // Read before the write replaces it: `fields` is what this save changed.
    const fields = changedDashboardFields(body, this.dashboards.get(id));
    try {
      const updated = await this.rootStore.dashboardService.update(slug, id, body);
      runInAction(() => {
        this.dashboards.set(updated.id, updated);
      });
      track("dashboard_updated", { dashboard_id: id, fields, source });
    } catch (err) {
      console.error("dashboard.store: updateDashboard failed", { slug, id, body, err });
      runInAction(() => {
        this.errorSavingDashboard.set(id, err as Error);
      });
    } finally {
      runInAction(() => {
        this.savingDashboard.set(id, false);
      });
    }
  }

  async updateDashboardFilters(
    slug: string,
    id: string,
    filters: Record<string, unknown>,
  ): Promise<void> {
    const version = (this.dashboardFilterSaveVersion.get(id) ?? 0) + 1;
    this.dashboardFilterSaveVersion.set(id, version);
    this.savingDashboardFilters.set(id, true);
    this.errorSavingDashboardFilters.set(id, null);

    const previous = this.dashboardFilterSaveQueue.get(id);
    const request = (previous?.catch(() => undefined) ?? Promise.resolve()).then(() =>
      this.rootStore.dashboardService.update(slug, id, { filters }),
    );
    this.dashboardFilterSaveQueue.set(id, request);

    try {
      const updated = await request;
      runInAction(() => {
        if (this.dashboardFilterSaveVersion.get(id) === version) {
          this.dashboards.set(updated.id, updated);
        }
      });
    } catch (err) {
      console.error("dashboard.store: updateDashboardFilters failed", {
        slug,
        id,
        filters,
        err,
      });
      if (this.dashboardFilterSaveVersion.get(id) !== version) return;
      runInAction(() => {
        this.errorSavingDashboardFilters.set(id, err as Error);
      });
      throw err;
    } finally {
      runInAction(() => {
        if (this.dashboardFilterSaveVersion.get(id) === version) {
          this.savingDashboardFilters.set(id, false);
        }
        if (this.dashboardFilterSaveQueue.get(id) === request) {
          this.dashboardFilterSaveQueue.delete(id);
        }
      });
    }
  }

  /** Re-fetch chart data for every widget on a dashboard. Used after the
   *  dashboard's project scope or active filter map changes so charts
   *  pick up the new query. */
  async refetchAllCharts(
    slug: string,
    dashboardId: string,
    filters?: URLSearchParams,
  ): Promise<void> {
    const widgets = this.widgetsByDashboard.get(dashboardId) ?? [];
    await Promise.all(widgets.map((dw) => this.fetchChart(slug, dashboardId, dw.id, filters)));
  }

  /** Set the per-widget capacity scope override and re-fetch just that widget.
   *  ``filters`` is the dashboard's current toolbar query so the refetch keeps
   *  date / project / etc. scoping; ``fetchChart`` layers the per-widget
   *  ``capacity_scope`` on top. */
  setCapacityScope(
    slug: string,
    dashboardId: string,
    widgetId: string,
    scope: "all" | "over_limit",
    filters?: URLSearchParams,
  ): void {
    runInAction(() => {
      this.capacityScopeByWidget.set(widgetId, scope);
    });
    void this.fetchChart(slug, dashboardId, widgetId, filters);
  }

  /** Capacity PR3 — toggle the expanded user under a W3 widget. */
  toggleExpandedUser(widgetId: string, userId: string): void {
    runInAction(() => {
      const current = this.expandedUserByWidget.get(widgetId) ?? null;
      this.expandedUserByWidget.set(widgetId, current === userId ? null : userId);
    });
  }

  /** Issue 1082: open the member detail panel for an owner/lead clicked in a table. */
  openMemberPanel(userId: string): void {
    runInAction(() => {
      this.panelMemberId = userId;
    });
  }

  /** Issue 1082: close the member detail panel. */
  closeMemberPanel(): void {
    runInAction(() => {
      this.panelMemberId = null;
    });
  }

  selectWidget(widgetId: string, options?: { focusName?: boolean }): void {
    runInAction(() => {
      this.selectedWidgetId = widgetId;
      this.focusNameOnOpen = options?.focusName ?? false;
    });
  }

  clearSelectedWidget(): void {
    runInAction(() => {
      this.selectedWidgetId = null;
      this.focusNameOnOpen = false;
    });
  }

  async createDashboard(
    slug: string,
    body: DashboardCreateBody,
    source: AnalyticsSource = "page",
  ): Promise<Dashboard> {
    try {
      const created = await this.rootStore.dashboardService.create(slug, body);
      runInAction(() => {
        this.dashboards.set(created.id, created);
      });
      track("dashboard_created", { dashboard_id: created.id, source });
      return created;
    } catch (err) {
      console.error("dashboard.store: createDashboard failed", { slug, body, err });
      throw err;
    }
  }

  /** NG-1260 — duplicate a dashboard into a private copy owned by the caller.
   *  The copy joins the list map so navigating to it renders without a refetch. */
  async duplicateDashboard(
    slug: string,
    id: string,
    source: AnalyticsSource = "page",
  ): Promise<Dashboard> {
    try {
      const copy = await this.rootStore.dashboardService.duplicate(slug, id);
      runInAction(() => {
        this.dashboards.set(copy.id, copy);
      });
      track("dashboard_duplicated", { dashboard_id: copy.id, duplicated_from_id: id, source });
      return copy;
    } catch (err) {
      console.error("dashboard.store: duplicateDashboard failed", { slug, id, err });
      throw err;
    }
  }

  async deleteDashboard(slug: string, id: string, source: AnalyticsSource = "page"): Promise<void> {
    try {
      await this.rootStore.dashboardService.deleteDashboard(slug, id);
      runInAction(() => {
        this.dashboards.delete(id);
        this.widgetsByDashboard.delete(id);
      });
      track("dashboard_deleted", { dashboard_id: id, source });
    } catch (err) {
      console.error("dashboard.store: deleteDashboard failed", { slug, id, err });
      throw err;
    }
  }

  async createWidget(
    slug: string,
    dashboardId: string,
    body: WidgetCreateBody,
    source: AnalyticsSource = "page",
  ): Promise<DashboardWidget> {
    try {
      const dw = await this.rootStore.dashboardService.createWidget(slug, dashboardId, body);
      runInAction(() => {
        const list = this.widgetsByDashboard.get(dashboardId) ?? [];
        this.widgetsByDashboard.set(dashboardId, [...list, dw]);
        this.bumpWidgetListGeneration(dashboardId);
      });
      track("dashboard_widget_added", { dashboard_id: dashboardId, widget_id: dw.id, source });
      return dw;
    } catch (err) {
      console.error("dashboard.store: createWidget failed", { slug, dashboardId, body, err });
      throw err;
    }
  }

  async updateWidget(
    slug: string,
    dashboardId: string,
    placementId: string,
    body: WidgetUpdateBody,
    source: AnalyticsSource = "page",
  ): Promise<DashboardWidget> {
    try {
      const updated = await this.rootStore.dashboardService.updateWidget(
        slug,
        dashboardId,
        placementId,
        body,
      );
      runInAction(() => {
        const list = this.widgetsByDashboard.get(dashboardId) ?? [];
        this.widgetsByDashboard.set(
          dashboardId,
          list.map((dw) => (dw.id === placementId ? updated : dw)),
        );
        this.bumpWidgetListGeneration(dashboardId);
      });
      // The editor panel sends only the keys it changed.
      track("dashboard_widget_updated", {
        dashboard_id: dashboardId,
        widget_id: placementId,
        fields: Object.keys(body),
        source,
      });
      return updated;
    } catch (err) {
      console.error("dashboard.store: updateWidget failed", {
        slug,
        dashboardId,
        placementId,
        body,
        err,
      });
      throw err;
    }
  }

  // Data layer for #1558 (drag/resize grid UI to follow). Calls the
  // bulk-update-widgets endpoint and merges the returned placements back into
  // widgetsByDashboard by id so only affected entries are replaced.
  async bulkUpdateWidgets(
    slug: string,
    dashboardId: string,
    updates: {
      id: string;
      x_axis_coord?: number;
      y_axis_coord?: number;
      width?: number;
      height?: number;
    }[],
    source: AnalyticsSource = "page",
  ): Promise<void> {
    try {
      const returned = await this.rootStore.dashboardService.bulkUpdateWidgets(
        slug,
        dashboardId,
        updates,
      );
      runInAction(() => {
        const list = this.widgetsByDashboard.get(dashboardId) ?? [];
        const byId = new Map(returned.map((dw) => [dw.id, dw]));
        this.widgetsByDashboard.set(
          dashboardId,
          list.map((dw) => byId.get(dw.id) ?? dw),
        );
        this.bumpWidgetListGeneration(dashboardId);
      });
      // One drag or resize is one layout write, however many cards it moved.
      track("dashboard_widget_updated", {
        dashboard_id: dashboardId,
        fields: ["layout"],
        count: updates.length,
        source,
      });
    } catch (err) {
      console.error("dashboard.store: bulkUpdateWidgets failed", {
        slug,
        dashboardId,
        updates,
        err,
      });
      throw err;
    }
  }

  async deleteWidget(
    slug: string,
    dashboardId: string,
    placementId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    try {
      await this.rootStore.dashboardService.deleteWidget(slug, dashboardId, placementId);
      runInAction(() => {
        const list = this.widgetsByDashboard.get(dashboardId) ?? [];
        this.widgetsByDashboard.set(
          dashboardId,
          list.filter((dw) => dw.id !== placementId),
        );
        this.bumpWidgetListGeneration(dashboardId);
        if (this.selectedWidgetId === placementId) this.selectedWidgetId = null;
      });
      track("dashboard_widget_removed", {
        dashboard_id: dashboardId,
        widget_id: placementId,
        source,
      });
    } catch (err) {
      console.error("dashboard.store: deleteWidget failed", {
        slug,
        dashboardId,
        placementId,
        err,
      });
      throw err;
    }
  }

  // --- Sharing (NG-1115) ---------------------------------------------------

  /** Load the dashboard's shares for the Share modal. Owner-only on the backend;
   *  a non-owner 403s and the modal simply never opens for them. */
  async loadShares(slug: string, dashboardId: string): Promise<void> {
    this.loadingShares.set(dashboardId, true);
    try {
      const shares = await this.rootStore.dashboardService.listShares(slug, dashboardId);
      runInAction(() => {
        this.sharesByDashboard.set(dashboardId, shares);
      });
    } catch (err) {
      console.error("dashboard.store: loadShares failed", { slug, dashboardId, err });
      throw err;
    } finally {
      runInAction(() => {
        this.loadingShares.set(dashboardId, false);
      });
    }
  }

  /** Share the dashboard with a user. Awaits the server row (it assigns the id
   *  and embeds the resolved user) before committing, so a create is not
   *  optimistic — the audience rule can also reject it with a field error. */
  async createShare(
    slug: string,
    dashboardId: string,
    body: DashboardShareCreateBody,
    source: AnalyticsSource = "page",
  ): Promise<DashboardShare> {
    try {
      const share = await this.rootStore.dashboardService.createShare(slug, dashboardId, body);
      runInAction(() => {
        const list = this.sharesByDashboard.get(dashboardId) ?? [];
        this.sharesByDashboard.set(dashboardId, [...list, share]);
      });
      track("dashboard_share_added", {
        dashboard_id: dashboardId,
        share_id: share.id,
        role: share.role,
        source,
      });
      return share;
    } catch (err) {
      console.error("dashboard.store: createShare failed", { slug, dashboardId, body, err });
      throw err;
    }
  }

  /** Change a recipient's role. Optimistic (revert on failure) — the role select
   *  reflects the change instantly. */
  async updateShareRole(
    slug: string,
    dashboardId: string,
    shareId: string,
    role: DashboardShareRole,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const snapshot = [...(this.sharesByDashboard.get(dashboardId) ?? [])];
    runInAction(() => {
      this.sharesByDashboard.set(
        dashboardId,
        snapshot.map((s) => (s.id === shareId ? { ...s, role } : s)),
      );
    });
    try {
      const updated = await this.rootStore.dashboardService.updateShare(
        slug,
        dashboardId,
        shareId,
        {
          role,
        },
      );
      runInAction(() => {
        const list = this.sharesByDashboard.get(dashboardId) ?? [];
        this.sharesByDashboard.set(
          dashboardId,
          list.map((s) => (s.id === shareId ? updated : s)),
        );
      });
      track("dashboard_share_updated", {
        dashboard_id: dashboardId,
        share_id: shareId,
        role,
        source,
      });
    } catch (err) {
      console.error("dashboard.store: updateShareRole failed", { slug, dashboardId, shareId, err });
      runInAction(() => {
        this.sharesByDashboard.set(dashboardId, snapshot); // revert
      });
      throw err;
    }
  }

  /** Remove a recipient's access. Optimistic (revert on failure). */
  async deleteShare(
    slug: string,
    dashboardId: string,
    shareId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const snapshot = [...(this.sharesByDashboard.get(dashboardId) ?? [])];
    runInAction(() => {
      this.sharesByDashboard.set(
        dashboardId,
        snapshot.filter((s) => s.id !== shareId),
      );
    });
    try {
      await this.rootStore.dashboardService.deleteShare(slug, dashboardId, shareId);
      track("dashboard_share_removed", { dashboard_id: dashboardId, share_id: shareId, source });
    } catch (err) {
      console.error("dashboard.store: deleteShare failed", { slug, dashboardId, shareId, err });
      runInAction(() => {
        this.sharesByDashboard.set(dashboardId, snapshot); // revert
      });
      throw err;
    }
  }

  /** Capacity PR3 — fetch the per-user breakdown for a single bar. Silent on
   *  failure: the pie renders an em-dash if the response is missing. */
  async fetchBreakdown(
    slug: string,
    dashboardId: string,
    userId: string,
    filters?: URLSearchParams,
  ): Promise<void> {
    this.loadingBreakdown.set(userId, true);
    try {
      const resp = await this.rootStore.dashboardService.getUserCapacityBreakdown(
        slug,
        dashboardId,
        userId,
        filters,
      );
      runInAction(() => {
        this.breakdownByUser.set(userId, resp);
      });
    } catch (err) {
      // Recoverable — pie shows em-dash.
      console.warn(
        "[dashboard] user capacity breakdown fetch failed",
        { slug, dashboardId, userId },
        err,
      );
    } finally {
      runInAction(() => {
        this.loadingBreakdown.set(userId, false);
      });
    }
  }

  /**
   * #2018 — derive the per-dashboard union of supported filter chips on the
   * frontend from the already-fetched widget metadata, replacing the retired
   * ``supported-filters/`` endpoint.
   *
   * Collects the distinct widget sources on the dashboard, unions each source's
   * served ``filters`` (from ``metadataBySource``), and orders the result by the
   * canonical chip order so the toolbar renders chips predictably.
   *
   * Returns ``null`` (meaning "not loaded yet — render everything") when the
   * data the union depends on hasn't arrived: either the widget metadata map is
   * empty, or this dashboard's widgets haven't been fetched. When the metadata
   * is loaded and the dashboard's widgets are present but empty, returns ``[]``
   * (no widgets ⇒ no filters).
   */
  supportedFiltersForDashboard(dashboardId: string): SupportedFilterKey[] | null {
    // Metadata not yet fetched — consumers preserve the "render everything"
    // fallback until the per-source capability map is available.
    if (this.metadataBySource.size === 0) return null;
    // Widgets for this dashboard not yet fetched (a missing entry is distinct
    // from an empty list). Render everything until they load.
    const widgets = this.widgetsByDashboard.get(dashboardId);
    if (widgets === undefined) return null;

    const merged = new Set<SupportedFilterKey>();
    const seenSources = new Set<string>();
    for (const dw of widgets) {
      const source = dw.widget.source;
      if (!source || seenSources.has(source)) continue;
      seenSources.add(source);
      const meta = this.metadataBySource.get(source);
      if (!meta) continue;
      for (const key of meta.filters) merged.add(key);
    }

    return CANONICAL_FILTER_ORDER.filter((key) => merged.has(key));
  }

  /** #1972 — fetch the per-source builder capability map (cached per slug). */
  async fetchWidgetMetadata(slug: string): Promise<void> {
    if (this.loadingWidgetMetadata) return;
    if (this.widgetMetadataSlug === slug && this.metadataBySource.size > 0) return;
    this.loadingWidgetMetadata = true;
    try {
      const sources = await this.rootStore.dashboardService.getWidgetMetadata(slug);
      runInAction(() => {
        this.metadataBySource = new Map(sources.map((s) => [s.value, s]));
        this.widgetMetadataSlug = slug;
      });
    } catch (err) {
      // Non-fatal: editor falls back to empty option lists + loading state.
      console.error("dashboard.store: fetchWidgetMetadata failed", { slug, err });
    } finally {
      runInAction(() => {
        this.loadingWidgetMetadata = false;
      });
    }
  }

  async fetchChart(
    slug: string,
    dashboardId: string,
    widgetId: string,
    filters?: URLSearchParams,
  ): Promise<void> {
    const version = (this.chartFetchVersion.get(widgetId) ?? 0) + 1;
    this.chartFetchVersion.set(widgetId, version);
    this.loadingChart.set(widgetId, true);
    this.errorChart.set(widgetId, null);
    // Layer this widget's capacity-scope override (if the user toggled it)
    // onto the shared toolbar query. Clone so we never mutate the caller's
    // params, and only when an override exists — absent → BE config default.
    const scope = this.capacityScopeByWidget.get(widgetId);
    const effectiveFilters = scope
      ? (() => {
          const next = new URLSearchParams(filters);
          next.set("capacity_scope", scope);
          return next;
        })()
      : filters;
    try {
      const resp = await this.rootStore.dashboardService.getChart(
        slug,
        dashboardId,
        widgetId,
        effectiveFilters,
      );
      runInAction(() => {
        if (this.chartFetchVersion.get(widgetId) !== version) return;
        this.chartByWidget.set(widgetId, resp);
      });
    } catch (err) {
      console.error("dashboard.store: fetchChart failed", { slug, dashboardId, widgetId, err });
      runInAction(() => {
        if (this.chartFetchVersion.get(widgetId) !== version) return;
        this.errorChart.set(widgetId, err as Error);
      });
    } finally {
      runInAction(() => {
        if (this.chartFetchVersion.get(widgetId) === version) {
          this.loadingChart.set(widgetId, false);
        }
      });
    }
  }
}
